<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    SimpleDivider,
    MfaVerification,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    allowedLoginMethods() {
      return window.chatwootConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.chatwootConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.chatwootConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      // Use a method to get the translated text to avoid dynamic key warning
      const translatedMessage = this.getTranslatedMessage(messageKey);
      toast.success(translatedMessage);
      // wait for idle state
      this.requestIdleCallbackPolyfill(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      // Avoid dynamic key warning by handling each case explicitly
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message, kind = 'error') {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      toast[kind](this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation === 'true') {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          // Check if MFA is required
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(
            this.$t('LOGIN.API.SUCCESS_MESSAGE'),
            'success'
          );
        })
        .catch(response => {
          // Reset URL Params if the authentication is invalid
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      // MFA verification successful, continue with login
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      // User cancelled MFA, reset state
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
  },
};
</script>

<template>
  <main
    class="login-page flex flex-col items-center justify-center w-full min-h-screen px-4 sm:px-6 lg:px-8"
  >
    <!-- Brand Header -->
    <section class="flex flex-col items-center mb-8">
      <svg width="56" height="56" viewBox="0 0 80 80" fill="none" class="mb-5">
        <defs>
          <linearGradient id="loginRose" x1="0" y1="0" x2="80" y2="80">
            <stop offset="0%" stop-color="#9B2C2C" />
            <stop offset="100%" stop-color="#BE185D" />
          </linearGradient>
        </defs>
        <rect width="80" height="80" rx="20" fill="url(#loginRose)" />
        <path
          d="M20 50c0-11.046 8.954-20 20-20s20 8.954 20 20"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
        />
        <path
          d="M28 50c0-6.627 5.373-12 12-12s12 5.373 12 12"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
          opacity="0.6"
        />
        <path
          d="M36 50c0-2.209 1.791-4 4-4s4 1.791 4 4"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
          opacity="0.3"
        />
      </svg>
      <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <h1 class="login-brand-text text-3xl font-bold text-white tracking-tight">
        app<span class="login-brand-dot">.</span>smart
      </h1>
      <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <p class="mt-3 text-sm text-white/60">
        {{ replaceInstallationName($t('LOGIN.TITLE')) }}
      </p>
      <p v-if="showSignupLink" class="mt-2 text-sm text-white/50">
        {{ $t('COMMON.OR') }}
        <router-link
          to="auth/signup"
          class="text-white/80 hover:text-white underline"
        >
          {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
        </router-link>
      </p>
    </section>

    <!-- MFA Verification Section -->
    <section v-if="mfaRequired" class="w-full max-w-md">
      <MfaVerification
        :mfa-token="mfaToken"
        @verified="handleMfaVerified"
        @cancel="handleMfaCancel"
      />
    </section>

    <!-- Regular Login Section -->
    <section
      v-else
      class="login-card w-full max-w-md p-8 sm:p-10 rounded-2xl"
      :class="{ 'animate-wiggle': loginApi.hasErrored }"
    >
      <div v-if="!email">
        <div class="flex flex-col gap-4">
          <GoogleOAuthButton v-if="showGoogleOAuth" />
          <div v-if="showSamlLogin" class="text-center">
            <router-link
              to="/app/login/sso"
              class="inline-flex justify-center w-full px-4 py-3 items-center rounded-xl border border-white/10 text-white/80 hover:bg-white/5 transition-colors"
            >
              <Icon icon="i-lucide-lock-keyhole" class="size-5 text-white/60" />
              <span class="ml-2 text-base font-medium text-white/90">
                {{ $t('LOGIN.SAML.LABEL') }}
              </span>
            </router-link>
          </div>
          <SimpleDivider
            v-if="showGoogleOAuth || showSamlLogin"
            :label="$t('COMMON.OR')"
            class="uppercase login-divider"
          />
        </div>
        <form class="space-y-5" @submit.prevent="submitFormLogin">
          <FormInput
            v-model="credentials.email"
            name="email_address"
            type="text"
            data-testid="email_input"
            :tabindex="1"
            required
            :label="$t('LOGIN.EMAIL.LABEL')"
            :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
            :has-error="v$.credentials.email.$error"
            @input="v$.credentials.email.$touch"
          />
          <FormInput
            v-model="credentials.password"
            type="password"
            name="password"
            data-testid="password_input"
            required
            :tabindex="2"
            :label="$t('LOGIN.PASSWORD.LABEL')"
            :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
            :has-error="v$.credentials.password.$error"
            @input="v$.credentials.password.$touch"
          >
            <p v-if="!globalConfig.disableUserProfileUpdate">
              <router-link
                to="auth/reset/password"
                class="text-sm text-white/50 hover:text-white/80 transition-colors"
                tabindex="4"
              >
                {{ $t('LOGIN.FORGOT_PASSWORD') }}
              </router-link>
            </p>
          </FormInput>
          <button
            type="submit"
            data-testid="submit_button"
            class="login-submit-btn w-full py-3 px-4 rounded-xl text-white font-semibold text-sm transition-all duration-200 disabled:opacity-50"
            :tabindex="3"
            :disabled="loginApi.showLoading"
          >
            <Spinner
              v-if="loginApi.showLoading"
              color-scheme="primary"
              size="small"
              class="inline mr-2"
            />
            {{ $t('LOGIN.SUBMIT') }}
          </button>
        </form>
      </div>
      <div v-else class="flex items-center justify-center py-8">
        <Spinner color-scheme="primary" size="" />
      </div>
    </section>

    <!-- Footer -->
    <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
    <p class="mt-8 text-xs text-white/30">app.smart.com.br</p>
    <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  </main>
</template>

<style scoped>
.login-page {
  background: #1c1917;
  background-image: radial-gradient(
      ellipse at 20% 50%,
      rgba(155, 44, 44, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 80% 50%,
      rgba(190, 24, 93, 0.1) 0%,
      transparent 50%
    );
}

.login-brand-dot {
  background: linear-gradient(135deg, #9b2c2c 0%, #be185d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.login-card {
  background: rgba(41, 37, 36, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

/* Override form input styles inside the dark login card */
.login-card :deep(label) {
  color: rgba(255, 255, 255, 0.7) !important;
}
.login-card :deep(input) {
  background: rgba(28, 25, 23, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}
.login-card :deep(input:focus) {
  border-color: #be185d !important;
  box-shadow: 0 0 0 3px rgba(190, 24, 93, 0.2) !important;
}
.login-card :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.3) !important;
}

.login-submit-btn {
  background: linear-gradient(135deg, #9b2c2c 0%, #be185d 100%);
  box-shadow: 0 4px 14px rgba(155, 44, 44, 0.4);
}
.login-submit-btn:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(155, 44, 44, 0.6);
  transform: translateY(-1px);
}

.login-divider :deep(span) {
  color: rgba(255, 255, 255, 0.3) !important;
}
.login-divider :deep(hr),
.login-divider :deep(.border-n-weak) {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
